// TMOS Plan Card Component
// Date: January 13, 2026
// Purpose: Card view for Plan entities

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, ChartBarIcon, EyeIcon, PencilIcon } from '@heroicons/react/24/outline';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { HealthIndicator } from './HealthIndicator';
import { Plan } from '@/types/plan';
import { PlanHealth } from '@/types/businessPerformance';

export interface PlanCardProps {
  plan: Plan;
  health?: PlanHealth;
  onView?: (plan: Plan) => void;
  onEdit?: (plan: Plan) => void;
  onClick?: (planId: string) => void;
}

export const PlanCard: React.FC<PlanCardProps> = ({
  plan,
  health,
  onView,
  onEdit,
  onClick
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(plan.id);
    } else if (onView) {
      onView(plan);
    } else {
      navigate(`/performance/plans/${plan.id}`);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (onEdit) {
      onEdit(plan);
    } else {
      navigate(`/performance/plans/${plan.id}/edit`);
    }
  };

  const getPlanTypeColor = (type: string) => {
    switch (type) {
      case 'STRATEGIC':
        return 'bg-purple-100 text-purple-800';
      case 'ANNUAL':
        return 'bg-blue-100 text-blue-800';
      case 'QUARTERLY':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'PROPOSED':
        return 'bg-yellow-100 text-yellow-800';
      case 'DRAFT':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <Card
      variant="outline"
      padding="none"
      className="hover:shadow-md transition-shadow cursor-pointer h-full flex flex-col"
      onClick={handleClick}
    >
      <CardContent className="p-4 flex-1 flex flex-col">
        {/* Header: Type and Status */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <ChartBarIcon className="w-5 h-5 text-[var(--color-info)] flex-shrink-0" />
            <Badge className={`text-xs ${getPlanTypeColor(plan.plan_type)}`}>
              {plan.plan_type}
            </Badge>
            <Badge className={`text-xs ${getStatusColor(plan.status)}`}>
              {plan.status}
            </Badge>
          </div>
          {health && (
            <HealthIndicator status={health.status} score={health.health_score} size="sm" />
          )}
        </div>

        {/* Name */}
        <h3 className="font-semibold text-[var(--color-secondary-text)] truncate mb-2">
          {plan.name}
        </h3>

        {/* Description */}
        {plan.description && (
          <p className="text-sm text-[var(--color-secondary-text)] opacity-75 line-clamp-2 mb-3">
            {plan.description}
          </p>
        )}

        {/* Metadata */}
        <div className="space-y-2 mb-3 flex-1">
          <div className="flex items-center gap-2 text-xs text-[var(--color-secondary-text)] opacity-75">
            <CalendarIcon className="w-4 h-4" />
            <span>
              {plan.plan_year}
              {plan.plan_quarter && ` Q${plan.plan_quarter}`}
            </span>
          </div>
          {health && (
            <div className="text-xs text-[var(--color-secondary-text)] opacity-75">
              {health.objectives_count} objectives • {health.objectives_on_track} on track
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200">
          {onView && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView(plan);
              }}
              className="p-1 text-[var(--color-secondary-text)] hover:text-[var(--color-primary)] transition-colors"
              title="View"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
          )}
          {onEdit && (
            <button
              onClick={handleEdit}
              className="p-1 text-[var(--color-secondary-text)] hover:text-[var(--color-primary)] transition-colors"
              title="Edit"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

